// apps/web/src/components/reports/team-performance/TeamPerformanceFilters.tsx
/**
 * Team Performance — the filter drawer.
 *
 * Opens on arrival and holds the report closed until it is satisfied: Start
 * Date, End Date and Campaign are required, matching Agent Performance's
 * drawer. Team narrows the campaign's roster and is cleared whenever the
 * campaign selection changes, since it is scoped to it.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { Calendar } from 'primereact/calendar'
import { SlidersHorizontal, RotateCcw, UsersRound } from 'lucide-react'

import ResourceDrawer from '../../admin/ResourceDrawer.js'
import DualListSelect from '../../common/DualListSelect.js'
import {
  useTeamPerformanceFilterOptions,
  type TeamReportFilters,
} from '../../../hooks/useTeamPerformanceReport.js'
import { useOrgTimezone } from '../../../hooks/useOrgTimezone.js'

export const INTERVAL_OPTIONS = [
  { label: '30 Minutes', value: '30m' },
  { label: '60 Minutes Hourly', value: '60m' },
  { label: '24 Hours Day Wise', value: '1d' },
]

interface TeamPerformanceFiltersProps {
  open: boolean
  onClose: () => void
  onRun: (filters: TeamReportFilters) => void
  onReset: () => void
  running: boolean
}

/** `YYYY-MM-DD` in LOCAL time — `toISOString()` would shift the day westward. */
function toDateString(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** `HH:MM` in local time; empty when unset — the backend defaults to the whole day. */
function toTimeString(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function TeamPerformanceFilters({
  open,
  onClose,
  onRun,
  onReset,
  running,
}: TeamPerformanceFiltersProps): JSX.Element {
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const { timezone: orgTimezone } = useOrgTimezone()

  // Start on the resolved timezone if it's already cached (no flash), or the
  // browser zone as a placeholder while the first fetch is in flight.
  const [timezone, setTimezone] = useState<string>(orgTimezone || browserZone)

  // Once the resolved timezone arrives, adopt it — but only if the user hasn't
  // manually changed the dropdown yet (timezone still matches the initial value).
  useEffect(() => {
    if (orgTimezone) setTimezone(orgTimezone)
  }, [orgTimezone])
  const [interval, setInterval] = useState<string>('1d')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [endTime, setEndTime] = useState<Date | null>(null)
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [teamIds, setTeamIds] = useState<string[]>([])
  const [touched, setTouched] = useState(false)

  const { options, loading } = useTeamPerformanceFilterOptions(campaignIds)

  const timezoneOptions = useMemo(() => {
    const list = options.timezones.map((t) => ({
      label: t.offset ? `${t.label} (${t.offset})` : t.label,
      value: t.value,
    }))
    if (!list.some((t) => t.value === browserZone)) {
      list.unshift({ label: browserZone, value: browserZone })
    }
    return list
  }, [options.timezones, browserZone])

  // Changing campaign invalidates team — it is scoped to it, and a stale id
  // would filter against a roster that no longer applies. A previously
  // selected team that has dropped out of the newly-scoped list is cleared
  // here rather than left dangling in the picker's value.
  const campaignKey = campaignIds.join(',')
  useEffect(() => { setTeamIds([]) }, [campaignKey])

  // Once options.teams changes shape (new campaign resolved), drop any
  // selected team id no longer present, without waiting on campaignKey again
  // (covers the case where the campaign selection is unchanged but the
  // resolved team list still updates, e.g. after a retry).
  useEffect(() => {
    setTeamIds((prev) => {
      const known = new Set(options.teams.map((t) => t.value))
      const next = prev.filter((id) => known.has(id))
      return next.length === prev.length ? prev : next
    })
  }, [options.teams])

  const errors = {
    startDate: !startDate ? 'Start date is required' : '',
    endDate: !endDate
      ? 'End date is required'
      : startDate && endDate < startDate
        ? 'End date must be on or after start date'
        : '',
    campaignId: campaignIds.length === 0 ? 'At least one campaign is required' : '',
  }
  const valid = !errors.startDate && !errors.endDate && !errors.campaignId

  const handleRun = (): void => {
    setTouched(true)
    if (!valid) return
    onRun({
      timezone,
      interval,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      startTime: toTimeString(startTime),
      endTime: toTimeString(endTime),
      campaignIds,
      teamIds,
    })
  }

  const handleReset = (): void => {
    setTimezone(orgTimezone)
    setInterval('1d')
    setStartDate(null)
    setEndDate(null)
    setStartTime(null)
    setEndTime(null)
    setCampaignIds([])
    setTeamIds([])
    setTouched(false)
    onReset()
  }

  const showError = (msg: string): JSX.Element | null =>
    touched && msg ? <small className="mt-1 block text-xs font-medium text-red-600">{msg}</small> : null

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => { handleRun() }}
      saving={running}
      disabled={touched && !valid}
      width="xl"
      saveLabel={running ? 'Running…' : 'Run Report'}
      title="Report Filters"
    >
      <div className="kc3-form">
      <p className="mb-4 flex items-center gap-2 text-xs text-slate-500">
        <SlidersHorizontal className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
        Choose a window and campaign, then run the report. Times are optional — leave them empty for whole days.
      </p>

      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Reporting window</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Timezone">
          <Dropdown
            value={timezone}
            options={timezoneOptions}
            onChange={(e) => setTimezone(String(e.value))}
            filter
            placeholder="Select timezone"
            className="w-full"
          />
        </Field>

        <Field label="Interval Type">
          <Dropdown
            value={interval}
            options={INTERVAL_OPTIONS}
            onChange={(e) => setInterval(String(e.value))}
            className="w-full"
          />
        </Field>

        <Field label="Start Date" required>
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate((e.value as Date | null) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
            placeholder="Select start date"
            className={`w-full ${touched && errors.startDate ? 'p-invalid' : ''}`}
            maxDate={endDate ?? undefined}
          />
          {showError(errors.startDate)}
        </Field>

        <Field label="Start Time">
          <Calendar
            value={startTime}
            onChange={(e) => setStartTime((e.value as Date | null) ?? null)}
            timeOnly
            hourFormat="24"
            showIcon
            icon="pi pi-clock"
            placeholder="00:00"
            className="w-full"
          />
        </Field>

        <Field label="End Date" required>
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate((e.value as Date | null) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
            placeholder="Select end date"
            className={`w-full ${touched && errors.endDate ? 'p-invalid' : ''}`}
            minDate={startDate ?? undefined}
          />
          {showError(errors.endDate)}
        </Field>

        <Field label="End Time">
          <Calendar
            value={endTime}
            onChange={(e) => setEndTime((e.value as Date | null) ?? null)}
            timeOnly
            hourFormat="24"
            showIcon
            icon="pi pi-clock"
            placeholder="23:59"
            className="w-full"
          />
        </Field>

      </div>

      <p className="mb-3 mt-5 border-t border-slate-100 pt-4 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Scope</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Campaign" required>
          <MultiSelect
            value={campaignIds}
            options={options.campaigns}
            onChange={(e) => setCampaignIds((e.value as string[]) ?? [])}
            filter
            display="chip"
            maxSelectedLabels={2}
            selectedItemsLabel="{0} campaigns"
            placeholder={loading ? 'Loading…' : 'Select campaigns'}
            className={`w-full ${touched && errors.campaignId ? 'p-invalid' : ''}`}
          />
          {showError(errors.campaignId)}
        </Field>
      </div>

      {/* Team — the same dual-list picker Agent Performance uses for Agents,
          so "Team → Selected Teams" behaves identically in both drawers. */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <UsersRound className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
          <span className="text-xs font-semibold text-slate-600">Team</span>
          {teamIds.length > 0 && (
            <span className="rounded-[3px] border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700">
              {teamIds.length}
            </span>
          )}
          <span className="text-[11px] text-slate-400">— filtered by campaign</span>
        </div>
        <p className="mb-1.5 text-[11px] text-slate-400">
          Leave empty to include every team in the selected campaign(s).
        </p>
        <DualListSelect
          options={options.teams}
          selected={teamIds}
          onChange={setTeamIds}
          availableLabel="Teams"
          selectedLabel="Selected Teams"
          searchPlaceholder="Search teams…"
          height={180}
          emptyText={campaignIds.length === 0 ? 'Select a campaign to list teams' : 'No teams in this campaign'}
        />
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="text-xs text-slate-500">
          {teamIds.length > 0
            ? `${teamIds.length} team${teamIds.length === 1 ? '' : 's'} selected`
            : 'All teams in the selected campaign(s)'}
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reset
        </button>
      </div>
      </div>
    </ResourceDrawer>
  )
}

function Field({
  label,
  required = false,
  children,
}: { label: string; required?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span aria-hidden className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
